import { useLiveQueryV2, CODES } from '@libs/use';
import { UnderlyingAssetIsinsQuery } from '@libs/api-reference';
import {
  SYMBOL_TRADE_CHANNEL_PREFIX,
  SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX,
  SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX,
  transformTradePriceChange,
  transformSymbolTradingStateChange,
  transformClosingPriceChange,
} from '@core/symbol';

import * as API from '../requests';
import { OPTION_QUERY_KEYS } from '../constants';
import { computed, ComputedRef } from 'vue';

export default function useUnderlyingAssetIsins() {
  const {
    result,
    status: getUnderlyingAssetIsinsStatus,
    execute: getUnderlyingAssetIsins,
    isPending: isLoadingUnderlyingAssetIsins,
    isSuccess: isSuccessLoadingUnderlyingAssetIsins,
    isError: isErrorLoadingUnderlyingAssetIsins,
  } = useLiveQueryV2<UnderlyingAssetIsinsQuery>({
    fetcher: () => API.getUnderlyingAssetIsins(),
    key: OPTION_QUERY_KEYS.underlyingAssetIsins,
    immediate: true,
    cacheTime: 1000 * 60 * 60, // 1h
    staleTime: 1000 * 60 * 60, // 1h,
    getChannels(result) {
      const underlyingInstrumentIsinItems =
        result?.data.underlyingInstrumentIsinItems;

      return underlyingInstrumentIsinItems?.reduce(
        (chanels: string[], items) => {
          chanels.push(
            `${SYMBOL_TRADE_CHANNEL_PREFIX}${items.isin}`,
            `${SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX}${items.isin}`,
            `${SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX}${items.isin}`
          );

          return chanels;
        },
        []
      );
    },
    onUpdate(signal) {
      const actions = {
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
        [CODES.SymbolStateChangeSignal]: transformSymbolTradingStateChange,
        [CODES.TseInstrumentClosingPriceChangeSignal]:
          transformClosingPriceChange,
      };

      const underlyingInstrumentIsinItems =
        result.value?.data.underlyingInstrumentIsinItems;

      if (actions[signal.code] && result.value?.data) {
        const data = actions[signal.code](signal.data);

        const index = underlyingInstrumentIsinItems?.findIndex(
          (item) => item.isin === data.isin
        );

        if (underlyingInstrumentIsinItems && index && index !== -1) {
          const target = underlyingInstrumentIsinItems[index];

          Object.assign(target, data);
        }
      }
    },
  });

  const underlyingAssetIsins = computed(
    () => result.value?.data.underlyingInstrumentIsinItems || []
  );

  return {
    underlyingAssetIsins,
    getUnderlyingAssetIsinsStatus,
    getUnderlyingAssetIsins,
    isLoadingUnderlyingAssetIsins,
    isSuccessLoadingUnderlyingAssetIsins,
    isErrorLoadingUnderlyingAssetIsins,
  };
}
